import { watch } from 'vue'

import type { BillingReturnRuntime } from '../flows/billingQueryCallbacks'
import { BillingReturnFlow, type BillingReturnNotice } from '../flows/BillingReturnFlow'
import { registerCreditMessages } from '../i18n'
import { useCreditStore } from '../stores/creditStore'

import { useAccount, useAccountSession } from '@/features/account'
import { useLocaleStore } from '@/shared/i18n'
import { useToast } from '@/shared/ui'

export type BillingReturnTranslator = (key: string) => string

/** Bind the headless return flow to the live account/credit stores and shared toast service. */
export function useBillingReturnRuntime(translate: BillingReturnTranslator): BillingReturnRuntime {
  const account = useAccount()
  const accountSession = useAccountSession()
  const credits = useCreditStore()
  const locale = useLocaleStore()
  const toast = useToast()

  const notify = (notice: BillingReturnNotice): void => {
    toast[notice.tone](translate(notice.key))
  }
  const flow = new BillingReturnFlow({
    reloadAccount: () => account.loadSelectedAccountContext(true),
    refreshCredits: () => credits.refresh(),
    notify,
  })

  async function waitForBoot(): Promise<void> {
    if (accountSession.status.value !== 'booting') return
    await new Promise<void>((resolve) => {
      const stop = watch(accountSession.status, (status) => {
        if (status === 'booting') return
        stop()
        resolve()
      })
    })
  }

  async function prepare(): Promise<void> {
    await Promise.all([registerCreditMessages(locale.locale), waitForBoot()])
  }

  return {
    async handle(param, value) {
      await prepare()
      const outcome = await flow.execute(param, value)
      return { redirect: outcome.destination }
    },
    async rejectUnknown() {
      await prepare()
      notify({ tone: 'error', key: 'credits.billing.return.failed' })
      return { redirect: { name: 'Summary' } }
    },
  }
}
